import {ComponentProps, memo, useCallback, useMemo, useRef, useState} from "react";
import {Box, HStack, mergeProps, Stack, useDisclosure} from "@chakra-ui/react";
import {ZoomControl} from "./components/ui/ZoomControl";
import {Toolbar} from "./components/ui/Toolbar";
import {useKeyboardShortcuts} from "./utility/keyboardShortcuts";
import {
  Camera,
  useResizeCamera,
  useSmoothlyUpdateCameraPosition,
  useSmoothlyUpdateCameraZoom,
  useInitializeCameraView,
} from "./utility/camera";
import {PositionControl} from "./components/ui/PositionControl";
import {
  CELL_SIZE,
  useDiagramEditor,
  DiagramAnyObject,
  serializeAnyObjectId,
  getAnyObjectId,
  getAnyObject,
} from "./utility/types";
import {chooseCursor} from "./utility/cursor";
import {PropertiesPane} from "./components/ui/PropertiesPane";
import {BaseConnectionArrow, SelectionBorder, HoveredCell} from "./components/view";
import {DiagramContent} from "./components/view/DiagramContent";
import {Vec2} from "./utility/math/vec2";
import {Diagram, DiagramId, nominate} from "platformed-client/types";
import {q, useQueryData} from "../../../../../../state";
import {SaveState} from "./components/ui/SaveState";
import {InteractionsLayer} from "./components/view/InteractionsLayer";
import type {MockSourceEvidence} from "../../types";
import {usePanControls, useZoomControls} from "./utility/cameraControls";
import {defaultToolState, Tool, useToolEventHandlers} from "./utility/tools";
import {ExportDialog} from "./components/ui/ExportDialog";
import {JsonImportExport} from "./components/ui/JsonImportExport";
import {Internal} from "../../../../../../components/InternalMode";
import {UiPointerEventsProvider} from "./utility/uiPointerEventsContext";

export type SwimlaneConfig = {label: string; color: string; minX: number; maxX: number};

// Inner component that contains the actual editor logic
const DiagramEditorInner = memo(
  ({
    diagramId,
    showSwimlanes,
    swimlanes,
    initialDiagram,
    nodeCitations,
    evidence,
  }: {
    diagramId: DiagramId;
    showSwimlanes: boolean;
    swimlanes?: SwimlaneConfig[];
    initialDiagram: Diagram;
    nodeCitations?: Record<string, number[]>;
    evidence?: MockSourceEvidence[];
  }) => {
    const parentRef = useRef<HTMLDivElement>(null);

    const [camera, setCamera] = useState<Camera>(Camera.default());
    const [isPanning, setIsPanning] = useState<boolean>(false);
    const [isHoldingSpace, setIsHoldingSpace] = useState<boolean>(false);
    const [isHoldingAlt, setIsHoldingAlt] = useState<boolean>(false);
    const [hoverCellPosition, setHoverCellPosition] = useState<Vec2 | null>(null);
    const exportDialog = useDisclosure();

    const {diagram, spatialIndex, dispatch, saving, toolState, setToolState} = useDiagramEditor(
      diagramId,
      initialDiagram,
    );

    useInitializeCameraView(camera, setCamera, diagram);

    const isDragging = useMemo(() => toolState.tool === "select" && toolState.dragging != null, [toolState]);

    const selectedObject = useMemo<DiagramAnyObject | null>(() => {
      if (toolState.tool !== "select" || toolState.selectedObjectId == null) {
        return null;
      }

      return getAnyObject(toolState.selectedObjectId, diagram);
    }, [diagram, toolState]);

    const updateObject = useCallback(
      (object: DiagramAnyObject) => {
        switch (object.type) {
          case "Node":
            {
              dispatch({action: "updateNode", node: object.node});
            }
            break;
          case "Connection":
            {
              dispatch({action: "updateConnection", connection: object.connection});
            }
            break;
          default:
            break;
        }
      },
      [dispatch],
    );

    const duplicateObject = useCallback(
      (object: DiagramAnyObject) => {
        if (object.type !== "Node") {
          return;
        }

        const duplicateNode = {
          ...object.node,
          id: nominate("DiagramNodeId", window.crypto.randomUUID()),
          position: object.node.position.add(new Vec2(1, 1)),
        };

        dispatch({action: "createNode", node: duplicateNode});

        setCamera(prev => {
          const next = prev.clone();
          next.targetPosition = duplicateNode.position.mulScalar(CELL_SIZE).addScalar(0.5 * CELL_SIZE);
          return next;
        });

        setToolState({tool: "select", selectedObjectId: {type: "Node", id: duplicateNode.id}, dragging: null});
      },
      [dispatch, setCamera, setToolState],
    );

    const deleteObject = useCallback(
      (object: DiagramAnyObject) => {
        // Ensure the object is no longer selected.
        setToolState(prev => {
          if (
            prev.tool !== "select" ||
            prev.selectedObjectId == null ||
            serializeAnyObjectId(prev.selectedObjectId) !== serializeAnyObjectId(getAnyObjectId(object))
          ) {
            return prev;
          }

          return {...prev, selectedObjectId: null};
        });

        switch (object.type) {
          case "Node":
            {
              dispatch({action: "deleteNode", nodeId: object.node.id});
            }
            break;

          case "Connection":
            {
              dispatch({action: "deleteConnection", connectionId: object.connection.id});
            }
            break;

          case "ConnectionAnchor":
            {
              dispatch({
                action: "updateConnection",
                connection: {
                  ...object.connection,
                  // Remove the anchor.
                  anchors: [
                    ...object.connection.anchors.slice(0, object.index),
                    ...object.connection.anchors.slice(object.index + 1),
                  ],
                },
              });
            }
            break;

          default:
            break;
        }
      },
      [dispatch, setToolState],
    );

    useKeyboardShortcuts(
      useMemo(
        () => ({setToolState, setCamera, setIsHoldingSpace, setIsHoldingAlt, selectedObject, deleteObject}),
        [deleteObject, selectedObject, setToolState],
      ),
    );

    useResizeCamera(parentRef, setCamera);

    const cursor = useMemo(
      () => chooseCursor({toolState, isHoldingSpace, isHoldingAlt, isPanning}),
      [toolState, isHoldingSpace, isHoldingAlt, isPanning],
    );

    const onPointerMove = useCallback(
      (ev: React.PointerEvent) => {
        const viewportRect = parentRef.current!.getBoundingClientRect();
        const screenPos = new Vec2(ev.clientX - viewportRect.left, ev.clientY - viewportRect.top);

        const worldPos = camera.s2w(screenPos);
        const cellCoords = worldPos.divScalar(CELL_SIZE).floor();

        // Update hover position
        setHoverCellPosition(prev => {
          if (prev?.equals(cellCoords)) {
            return prev;
          }
          return cellCoords;
        });
      },
      [camera],
    );

    const allowPanning = toolState.tool === "pan" || isHoldingSpace;

    const panCameraProps = usePanControls(allowPanning, setCamera, setIsPanning);
    const zoomCameraProps = useZoomControls(setCamera);
    useSmoothlyUpdateCameraZoom(setCamera);
    useSmoothlyUpdateCameraPosition(setCamera);

    const toolEventHandlers = useToolEventHandlers(
      useMemo(
        () => ({
          toolState,
          setToolState,
          containerElement: parentRef.current!,
          diagram,
          dispatchDiagram: dispatch,
          spatialIndex: spatialIndex.current!,
          camera,
          setCamera,
        }),
        [camera, diagram, dispatch, setToolState, spatialIndex, toolState],
      ),
    );

    const interactionsLayerProps = useMemo(
      () =>
        mergeProps<ComponentProps<typeof InteractionsLayer>>(
          {onPointerMove},
          panCameraProps,
          zoomCameraProps,
          toolEventHandlers,
        ),
      [onPointerMove, panCameraProps, toolEventHandlers, zoomCameraProps],
    );

    const onSelectedToolChange = useCallback(
      (tool: Tool) => {
        setToolState(defaultToolState(tool));
      },
      [setToolState],
    );

    return (
      <Stack
        flex="1 1 0px"
        minH="320px"
        position="relative"
        borderWidth="1px"
        rounded="md"
        overflow="hidden"
        backgroundColor="#f6faff"
        ref={parentRef}
        style={{cursor}}
      >
        <InteractionsLayer {...interactionsLayerProps} />
        <DiagramContent
          diagram={diagram}
          camera={camera}
          showBackgroundGrid
          showSwimlanes={showSwimlanes}
          swimlanes={swimlanes}
          nodeCitations={nodeCitations}
          evidence={evidence}
          extraElements={
            <>
              {hoverCellPosition != null && <HoveredCell position={hoverCellPosition} />}
              {/* Show selection border around objects that occupy a single cell. */}
              {selectedObject != null &&
                (selectedObject.type === "Node" ? (
                  <SelectionBorder position={selectedObject.node.position} />
                ) : selectedObject.type === "ConnectionAnchor" ? (
                  <SelectionBorder position={selectedObject.anchor.position} />
                ) : null)}
              {toolState.tool === "createConnection" && toolState.drawing != null && (
                <BaseConnectionArrow
                  waypoints={[toolState.drawing.start, toolState.drawing.end]}
                  colorPalette={"blue"}
                />
              )}
            </>
          }
        />

        <Box
          position="absolute"
          top={0}
          left={0}
          bottom={0}
          right={0}
          pointerEvents="none"
          opacity={isDragging ? 0.6 : 1}
          transition="opacity 0.2s"
        >
          <UiPointerEventsProvider value={isDragging ? undefined : "auto"}>
            <Stack position="absolute" top={0} left={0} bottom={0} m={4} gap={4} justifyContent="space-between">
              <Toolbar
                selectedTool={toolState.tool}
                onSelectedToolChange={onSelectedToolChange}
                onClickDownload={exportDialog.onOpen}
              />
              <SaveState saving={saving} />
            </Stack>
            <Stack
              position="absolute"
              flexDirection="column-reverse"
              right={0}
              top={0}
              bottom={0}
              m={4}
              gap={4}
              alignItems="flex-end"
            >
              <HStack flex="0 0 auto">
                <Internal>
                  <JsonImportExport diagramId={diagramId} diagram={diagram} />
                </Internal>
                <PositionControl camera={camera} setCamera={setCamera} />
                <ZoomControl camera={camera} setCamera={setCamera} />
              </HStack>
              <PropertiesPane
                selectedObject={selectedObject}
                onUpdateObject={updateObject}
                onDuplicateObject={duplicateObject}
                onDeleteObject={deleteObject}
              />
            </Stack>
          </UiPointerEventsProvider>
        </Box>
        <ExportDialog {...exportDialog} diagram={diagram} showSwimlanes={showSwimlanes} swimlanes={swimlanes} />
      </Stack>
    );
  },
);

// Wrapper for queried data
const DiagramEditorWithQuery = memo(
  ({
    diagramId,
    showSwimlanes,
    swimlanes,
    nodeCitations,
    evidence,
  }: {
    diagramId: DiagramId;
    showSwimlanes: boolean;
    swimlanes?: SwimlaneConfig[];
    nodeCitations?: Record<string, number[]>;
    evidence?: MockSourceEvidence[];
  }) => {
    const queriedDiagram = useQueryData(q.tprm.diagram(diagramId));

    return (
      <DiagramEditorInner
        diagramId={diagramId}
        showSwimlanes={showSwimlanes}
        swimlanes={swimlanes}
        initialDiagram={queriedDiagram}
        nodeCitations={nodeCitations}
        evidence={evidence}
      />
    );
  },
);

// Wrapper component that handles querying vs using hardcoded data
export const DiagramEditor = memo(
  ({
    diagramId,
    showSwimlanes = false,
    swimlanes,
    hardcodedData,
    nodeCitations,
    evidence,
  }: {
    diagramId: DiagramId;
    showSwimlanes?: boolean;
    swimlanes?: SwimlaneConfig[];
    hardcodedData?: Diagram;
    nodeCitations?: Record<string, number[]>;
    evidence?: MockSourceEvidence[];
  }) => {
    // If hardcoded data is provided, use it directly without querying backend
    if (hardcodedData) {
      return (
        <DiagramEditorInner
          diagramId={diagramId}
          showSwimlanes={showSwimlanes}
          swimlanes={swimlanes}
          initialDiagram={hardcodedData}
          nodeCitations={nodeCitations}
          evidence={evidence}
        />
      );
    }

    // Otherwise, query from backend
    return (
      <DiagramEditorWithQuery
        diagramId={diagramId}
        showSwimlanes={showSwimlanes}
        swimlanes={swimlanes}
        nodeCitations={nodeCitations}
        evidence={evidence}
      />
    );
  },
);
